import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import PageTitle from '../components/PageTitle';

const STEPS = ['DSC Type', 'Applicant Details', 'Documents', 'Review & Submit'];

const DSC_TYPES = [
  {
    type: 'Class 3 Individual',
    icon: '👤',
    desc: 'For individuals — ITR filing, MCA, tender, e-procurement',
    useCases: ['Income Tax filing', 'MCA/ROC', 'e-Tendering', 'EPF/ESIC'],
    isOrg: false,
  },
  {
    type: 'Class 3 Organisation',
    icon: '🏢',
    desc: 'For company directors, partners, authorised signatories',
    useCases: ['MCA filing', 'Company e-tendering', 'GST signing', 'ROC forms'],
    isOrg: true,
  },
  {
    type: 'DGFT',
    icon: '🌐',
    desc: 'Mandatory for DGFT portal — import/export licence, IEC',
    useCases: ['IEC application', 'DGFT portal', 'Export-import licence', 'EXIM policy'],
    isOrg: false,
  },
  {
    type: 'Document Signer',
    icon: '📝',
    desc: 'For bulk document signing, high-volume digital signing',
    useCases: ['Bulk invoice signing', 'PDF signing', 'Automated workflows', 'API-based signing'],
    isOrg: false,
  },
];

const VALIDITIES = [
  { label: '1 Year', price: '₹999',   desc: 'Best for new users' },
  { label: '2 Years', price: '₹1,499', desc: 'Most popular', popular: true },
  { label: '3 Years', price: '₹1,999', desc: 'Best value' },
];

const TOKEN_TYPES = [
  { type: 'USB Token', icon: '🔑', desc: 'Physical USB dongle — works offline. Standard for most use cases.' },
  { type: 'eSign Only', icon: '☁️', desc: 'Cloud-based signing — no hardware needed. For Aadhaar eSign workflows.' },
];

const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh'];

const ORG_TYPES = ['Private Limited', 'Public Limited', 'LLP', 'Partnership', 'Proprietorship', 'Trust', 'Society', 'Government'];

const INIT = {
  dscType: '', validity: '', tokenType: '',
  applicantName: '', designation: '', dob: '', email: '', phone: '', pan: '',
  organisationName: '', organisationType: '', cin: '',
  address: '', city: '', state: '', pincode: '',
  remarks: '',
};

function Field({ label, name, type='text', placeholder, required, span2, value, onChange, error, hint, children }) {
  return (
    <div className={`field${span2 ? ' span2' : ''}`}>
      <label>{label}{required && <span className="req">*</span>}</label>
      {children || <input name={name} type={type} placeholder={placeholder} value={value} onChange={onChange} className={error ? 'err' : ''} />}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="err-msg">⚠ {error}</span>}
    </div>
  );
}

// Multi-file upload (for Aadhar front + back)
function MultiFileZone({ label, name, hint, required, maxFiles, files, onChange }) {
  const handleAdd = e => {
    const newFiles = Array.from(e.target.files);
    const combined = [...(files || []), ...newFiles];
    if (maxFiles && combined.length > maxFiles) {
      alert(`Max ${maxFiles} files allowed`);
      return;
    }
    const oversized = newFiles.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length) { alert('Max 5MB per file'); return; }
    onChange(name, combined);
    e.target.value = '';
  };
  const remove = idx => onChange(name, (files || []).filter((_, i) => i !== idx));
  const hasFiles = files && files.length > 0;
  const canAddMore = !maxFiles || (files || []).length < maxFiles;

  return (
    <div className="field">
      <label>
        {label}{required && <span className="req">*</span>}
        {maxFiles && (
          <span style={{ marginLeft: '.5rem', background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.68rem', fontWeight: 700, padding: '.1rem .45rem', borderRadius: 4 }}>
            {(files || []).length}/{maxFiles} uploaded
          </span>
        )}
      </label>
      {hasFiles && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginBottom: '.5rem' }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, padding: '.4rem .75rem' }}>
              <span style={{ fontSize: '.82rem', color: 'var(--green)', fontWeight: 500 }}>📄 {f.name}</span>
              <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontWeight: 700, padding: '.1rem .3rem' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {canAddMore && (
        <label style={{ cursor: 'pointer' }}>
          <div className={`file-zone ${hasFiles ? 'has-file' : ''}`} style={{ padding: '.75rem' }}>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAdd} style={{ display: 'none' }} />
            <div style={{ fontSize: '1.1rem', marginBottom: '.15rem' }}>➕</div>
            <div className="file-zone-label" style={{ fontSize: '.82rem' }}>
              {hasFiles ? 'Add another file' : 'Click to upload'}
            </div>
            <div className="file-zone-sub">{hint}</div>
          </div>
        </label>
      )}
    </div>
  );
}

function FileZone({ label, name, hint, required, file, onChange }) {
  return (
    <div className="field">
      <label>{label}{required && <span className="req">*</span>}</label>
      <div className={`file-zone ${file ? 'has-file' : ''}`}>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => onChange(name, e.target.files[0])} />
        <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>{file ? '✅' : '📎'}</div>
        <div className="file-zone-label">{file ? 'Selected' : 'Click to upload'}</div>
        {file ? <div className="file-name">📄 {file.name}</div> : <div className="file-zone-sub">{hint || 'PDF, JPG, PNG — max 5MB'}</div>}
      </div>
    </div>
  );
}

export default function DscForm() {
  const navigate    = useNavigate();
  const { user }    = useAuth();

  const [step, setStep]       = useState(1);
  const [data, setData]       = useState({
    ...INIT,
    applicantName: user?.fullName || '',
    email:         user?.email    || '',
    phone:         user?.phone    || '',
    pan:           user?.pan      || '',
  });
  const [files, setFiles]         = useState({});
  const [multiFiles, setMultiFiles] = useState({ docAadhar: [] });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(null);

  const selectedDsc = DSC_TYPES.find(d => d.type === data.dscType);
  const isOrg = selectedDsc?.isOrg;

  const handle = e => {
    const { name, value } = e.target;
    const v = ['pan', 'cin'].includes(name) ? value.toUpperCase() : value;
    setData(d => ({ ...d, [name]: v }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleFile = (name, file) => {
    if (file && file.size > 5 * 1024 * 1024) { alert('Max 5MB per file'); return; }
    setFiles(f => ({ ...f, [name]: file }));
  };

  const handleMultiFile = (name, arr) => {
    setMultiFiles(f => ({ ...f, [name]: arr }));
  };

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!data.dscType)   e.dscType   = 'Select DSC type';
      if (!data.validity)  e.validity  = 'Select validity';
      if (!data.tokenType) e.tokenType = 'Select token type';
    }
    if (step === 2) {
      if (!data.applicantName.trim()) e.applicantName = 'Required';
      if (!data.dob.trim())           e.dob           = 'Required';
      if (!data.email.trim() || !/^[\w.+-]+@[\w-]+\.[a-z]{2,}$/i.test(data.email)) e.email = 'Invalid email';
      if (!data.phone.trim() || !/^[6-9]\d{9}$/.test(data.phone)) e.phone = 'Invalid mobile';
      if (!data.pan.trim()  || !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(data.pan)) e.pan = 'Invalid PAN';
      if (isOrg && !data.organisationName.trim()) e.organisationName = 'Required for organisation DSC';
      if (!data.address.trim()) e.address = 'Required';
      if (!data.city.trim())    e.city    = 'Required';
      if (!data.state)          e.state   = 'Required';
      if (!data.pincode.trim() || !/^\d{6}$/.test(data.pincode)) e.pincode = 'Invalid pincode';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    setLoading(true); setApiError('');
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(multiFiles).forEach(([k, arr]) => { if (arr && arr.length) arr.forEach(f => fd.append(k, f)); });
      const { data: res } = await api.post('/api/dsc/apply', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res);
    } catch (err) {
      setApiError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <>
        <PageTitle title="DSC Application Submitted" />
        <div className="page" style={{ maxWidth: 580 }}>
          <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ width: 80, height: 80, background: 'var(--bluel)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2.2rem' }}>🔐</div>
            <h2 style={{ color: 'var(--navy)', marginBottom: '.5rem' }}>Application Submitted!</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>DSC Application #{success.id} received.</p>
            <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <span className="badge badge-submitted">{success.dscType}</span>
              <span className="badge badge-under_review">{success.validity}</span>
              <span className="badge badge-filed">{success.tokenType}</span>
            </div>
            <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '1.25rem', textAlign: 'left', marginBottom: '2rem' }}>
              <div style={{ fontSize: '.78rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '.75rem' }}>What happens next</div>
              {[
                'Our team verifies your documents (1 working day)',
                'Application submitted to DSC provider (eMudhra / Sify)',
                'Video verification call scheduled with you',
                'DSC issued and dispatched to your address',
                'Tracking details shared on your dashboard',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '.75rem', marginBottom: '.5rem', fontSize: '.85rem', color: 'var(--muted)', alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, background: 'var(--navy)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                  {s}
                </div>
              ))}
            </div>
            <div className="alert alert-info" style={{ fontSize: '.83rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              📞 Our team will contact you at <strong>{success.phone}</strong> for the video verification call.
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard →</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title="DSC Application" />
      <div className="page-hero">
        <div className="page-hero-label">Digital Signature Certificate</div>
        <h1>Apply for DSC</h1>
        <p>Class 3, DGFT & Document Signer DSC — delivered in 2-3 working days</p>
      </div>

      {/* Progress */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 620, margin: '0 auto' }}>
          <div className="progress-track">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'active' : 'pending';
              return (
                <div key={label} className={`pt-step ${state}`}>
                  <div className="pt-circle">{state === 'done' ? '✓' : n}</div>
                  <div className="pt-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="page">
        <div className="card">
          {apiError && <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>⚠ {apiError}</div>}

          {/* ── Step 1: DSC Type ── */}
          {step === 1 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 1 of 4</div>
                <h2 className="step-title">Select DSC Type & Configuration</h2>
                <p className="step-desc">Choose the type of Digital Signature Certificate you need.</p>
              </div>

              {/* DSC Type cards */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)', display: 'block', marginBottom: '.75rem' }}>
                  DSC Type<span className="req">*</span>
                </label>
                {errors.dscType && <span className="err-msg" style={{ display: 'block', marginBottom: '.5rem' }}>⚠ {errors.dscType}</span>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  {DSC_TYPES.map(d => (
                    <div key={d.type}
                      onClick={() => handle({ target: { name: 'dscType', value: d.type } })}
                      style={{ border: `2px solid ${data.dscType === d.type ? 'var(--navy)' : 'var(--border)'}`, background: data.dscType === d.type ? 'var(--bluel)' : '#fff', borderRadius: 12, padding: '1.25rem', cursor: 'pointer', transition: 'all .2s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{d.icon}</span>
                        <span style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--navy)' }}>{d.type}</span>
                      </div>
                      <p style={{ fontSize: '.8rem', color: 'var(--muted)', marginBottom: '.6rem', lineHeight: 1.5 }}>{d.desc}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem' }}>
                        {d.useCases.map(u => (
                          <span key={u} style={{ background: 'var(--creamd)', color: 'var(--muted)', fontSize: '.68rem', padding: '.15rem .5rem', borderRadius: 4 }}>{u}</span>
                        ))}
                  {(multiFiles.docAadhar || []).map((f, i) => (
                    <span key={`aadhar-${i}`} className="badge badge-filed">📄 Aadhar {i === 0 ? '(Front)' : '(Back)'}: {f.name}</span>
                  ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Validity */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)', display: 'block', marginBottom: '.75rem' }}>
                  Validity<span className="req">*</span>
                </label>
                {errors.validity && <span className="err-msg" style={{ display: 'block', marginBottom: '.5rem' }}>⚠ {errors.validity}</span>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.75rem' }}>
                  {VALIDITIES.map(v => (
                    <div key={v.label}
                      onClick={() => handle({ target: { name: 'validity', value: v.label } })}
                      style={{ border: `2px solid ${data.validity === v.label ? 'var(--navy)' : 'var(--border)'}`, background: data.validity === v.label ? 'var(--bluel)' : v.popular ? '#FFFDF8' : '#fff', borderRadius: 10, padding: '1rem', cursor: 'pointer', textAlign: 'center', position: 'relative', transition: 'all .2s' }}>
                      {v.popular && <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: 'var(--gold)', color: 'var(--navy)', fontSize: '.65rem', fontWeight: 700, padding: '.15rem .6rem', borderRadius: 20, whiteSpace: 'nowrap' }}>Popular</div>}
                      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: 'var(--navy)', marginBottom: '.2rem' }}>{v.price}</div>
                      <div style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)' }}>{v.label}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: '.2rem' }}>{v.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Token type */}
              <div>
                <label style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)', display: 'block', marginBottom: '.75rem' }}>
                  Token Type<span className="req">*</span>
                </label>
                {errors.tokenType && <span className="err-msg" style={{ display: 'block', marginBottom: '.5rem' }}>⚠ {errors.tokenType}</span>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                  {TOKEN_TYPES.map(t => (
                    <div key={t.type}
                      onClick={() => handle({ target: { name: 'tokenType', value: t.type } })}
                      style={{ border: `2px solid ${data.tokenType === t.type ? 'var(--navy)' : 'var(--border)'}`, background: data.tokenType === t.type ? 'var(--bluel)' : '#fff', borderRadius: 10, padding: '1rem', cursor: 'pointer', transition: 'all .2s' }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '.4rem' }}>{t.icon}</div>
                      <div style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)', marginBottom: '.3rem' }}>{t.type}</div>
                      <div style={{ fontSize: '.77rem', color: 'var(--muted)', lineHeight: 1.5 }}>{t.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Applicant Details ── */}
          {step === 2 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 2 of 4</div>
                <h2 className="step-title">Applicant Details & Address</h2>
              </div>

              <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--navy)', marginBottom: '1rem' }}>👤 Personal Information</div>
              <div className="form-grid" style={{ marginBottom: '1.75rem' }}>
                <Field name="applicantName" label="Full Name (as per PAN)" required span2
                  placeholder="Rahul Kumar Sharma" value={data.applicantName} onChange={handle} error={errors.applicantName} />
                {isOrg && (
                  <Field name="designation" label="Designation" placeholder="Director / Partner / CEO"
                    value={data.designation} onChange={handle} />
                )}
                <Field name="dob" label="Date of Birth" type="date" required
                  value={data.dob} onChange={handle} error={errors.dob} hint="As per PAN card" />
                <Field name="pan" label="PAN Number" required placeholder="ABCDE1234F" maxLength={10}
                  value={data.pan} onChange={handle} error={errors.pan} />
                <Field name="email" label="Email Address" type="email" required placeholder="you@email.com"
                  value={data.email} onChange={handle} error={errors.email} />
                <Field name="phone" label="Mobile Number" required placeholder="9876543210" maxLength={10}
                  value={data.phone} onChange={handle} error={errors.phone} hint="For video verification call" />
              </div>

              {/* Organisation fields */}
              {isOrg && (
                <>
                  <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--navy)', marginBottom: '1rem' }}>🏢 Organisation Details</div>
                  <div className="form-grid" style={{ marginBottom: '1.75rem' }}>
                    <Field name="organisationName" label="Organisation Name" required span2
                      placeholder="ABC Pvt. Ltd." value={data.organisationName} onChange={handle} error={errors.organisationName} />
                    <div className="field">
                      <label>Organisation Type</label>
                      <select name="organisationType" value={data.organisationType} onChange={handle}>
                        <option value="">Select type...</option>
                        {ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <Field name="cin" label="CIN / Registration No." placeholder="U72900MH2020PTC123456"
                      value={data.cin} onChange={handle} hint="Company / LLP identification number" />
                  </div>
                </>
              )}

              <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--navy)', marginBottom: '1rem' }}>📍 Address</div>
              <div className="form-grid">
                <Field span2 name="address" label="Full Address" required
                  placeholder="Flat/House No., Street, Area" value={data.address} onChange={handle} error={errors.address} />
                <Field name="city" label="City" required placeholder="Mumbai"
                  value={data.city} onChange={handle} error={errors.city} />
                <div className="field">
                  <label>State<span className="req">*</span></label>
                  <select name="state" value={data.state} onChange={handle} className={errors.state ? 'err' : ''}>
                    <option value="">Select state...</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span className="err-msg">⚠ {errors.state}</span>}
                </div>
                <Field name="pincode" label="PIN Code" required placeholder="400001" maxLength={6}
                  value={data.pincode} onChange={handle} error={errors.pincode} />
              </div>
            </>
          )}

          {/* ── Step 3: Documents ── */}
          {step === 3 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 3 of 4</div>
                <h2 className="step-title">Upload Documents</h2>
                <p className="step-desc">All documents must be clear and readable. PDF, JPG, PNG — max 5MB each.</p>
              </div>

              <div className="alert alert-info" style={{ marginBottom: '1.5rem', fontSize: '.85rem' }}>
                📋 Required: PAN card + Aadhar + Passport photo.
                {isOrg && ' For organisation DSC: also upload Organisation proof (MOA/Partnership deed) and Authorisation letter.'}
              </div>

              <div className="form-grid">
                <FileZone name="docPan" label="PAN Card" required hint="Clear copy of PAN card — front side"
                  file={files.docPan} onChange={handleFile} />
                <MultiFileZone name="docAadhar" label="Aadhar Card" required
                  hint="Upload front & back separately (2 files)" maxFiles={2}
                  files={multiFiles.docAadhar} onChange={handleMultiFile} />
                <FileZone name="docPhoto" label="Passport Size Photo" required hint="Recent photo — white/light background"
                  file={files.docPhoto} onChange={handleFile} />
                {isOrg && (
                  <>
                    <FileZone name="docOrgProof" label="Organisation Proof" required={isOrg}
                      hint="MOA/AOA for Pvt Ltd, Partnership deed for LLP/Partnership"
                      file={files.docOrgProof} onChange={handleFile} />
                    <FileZone name="docAuthLetter" label="Authorisation Letter" required={isOrg}
                      hint="Board resolution / authorisation letter on company letterhead"
                      file={files.docAuthLetter} onChange={handleFile} />
                  </>
                )}
              </div>

              <div className="field" style={{ marginTop: '1rem' }}>
                <label>Remarks (optional)</label>
                <textarea name="remarks" rows={3}
                  placeholder="Any special instructions — e.g. delivery address if different, urgent requirement..."
                  value={data.remarks} onChange={handle} />
              </div>

              <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.83rem' }}>
                📹 <strong>Video Verification:</strong> After submission our team will schedule a brief video call for identity verification — mandatory for DSC issuance.
              </div>
            </>
          )}

          {/* ── Step 4: Review ── */}
          {step === 4 && (
            <>
              <div className="step-header">
                <div className="step-label">Step 4 of 4</div>
                <h2 className="step-title">Review & Submit</h2>
              </div>

              {/* Config summary */}
              <div style={{ display: 'flex', gap: '.6rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {[data.dscType, data.validity, data.tokenType].map(v => v && (
                  <span key={v} style={{ background: 'var(--navy)', color: '#fff', fontSize: '.82rem', fontWeight: 600, padding: '.35rem .9rem', borderRadius: 20 }}>{v}</span>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Applicant</div>
                  {[['Name', data.applicantName], ['PAN', data.pan], ['DOB', data.dob], ['Email', data.email], ['Phone', data.phone]].map(([l, v]) => v ? (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
                      <span style={{ color: 'var(--muted)' }}>{l}</span>
                      <span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ) : null)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Address</div>
                  {[['Address', data.address], ['City', data.city], ['State', data.state], ['Pincode', data.pincode]].map(([l, v]) => v ? (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
                      <span style={{ color: 'var(--muted)' }}>{l}</span>
                      <span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ) : null)}
                  {isOrg && data.organisationName && (
                    <div style={{ marginTop: '.75rem' }}>
                      <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.5rem' }}>Organisation</div>
                      {[['Name', data.organisationName], ['Type', data.organisationType], ['CIN', data.cin]].map(([l, v]) => v ? (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
                          <span style={{ color: 'var(--muted)' }}>{l}</span>
                          <span style={{ fontWeight: 500 }}>{v}</span>
                        </div>
                      ) : null)}
                    </div>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div>
                <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.6rem' }}>Documents</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                    {Object.entries(files).filter(([k, v]) => v).map(([k, v]) => (
                      <span key={k} className="badge badge-filed">📄 {v.name}</span>
                    ))}
                    {(multiFiles.docAadhar || []).map((f, i) => (
                      <span key={`aadhar-${i}`} className="badge badge-filed">📄 Aadhar {i === 0 ? '(Front)' : '(Back)'}: {f.name}</span>
                    ))}
                  </div>
                  {Object.keys(files).length === 0 && (multiFiles.docAadhar || []).length === 0 && (
                    <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>No documents uploaded</span>
                  )}
                </div>
              </div>

              <div className="alert alert-info" style={{ marginTop: '1.5rem', fontSize: '.83rem' }}>
                ⚠️ By submitting, you confirm that all information provided is accurate and you authorise MyTaxNiti to process your DSC application.
              </div>
            </>
          )}

          <div className="btn-row">
            {step > 1 ? <button className="btn btn-outline" onClick={prev}>← Back</button> : <span />}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Step {step} of 4</span>
              {step < 4
                ? <button className="btn btn-primary" onClick={next}>Continue →</button>
                : <button className="btn btn-gold" onClick={submit} disabled={loading}>
                    {loading ? <><span className="spinner" /> Submitting...</> : '🔐 Submit DSC Application'}
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
}